import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Linkedin } from "lucide-react";
import {
  DEFAULT_PUBLIC_UI_CONFIG,
  fetchPublicUiConfig,
  normalizePublicUiConfig,
} from "../utils/publicUiConfig";
import PublicPageWidgets from "../components/PublicPageWidgets";
import { FadeUp, PublicHeader, PublicQuickLinks } from "./CampusConnect";
import "../styles/CampusPublicChrome.css";

/* Same public-site chrome (header, footer, fonts, section styling) as the Campus Connect
   homepage. Leadership + developer content moved here from that page. */
export default function BehindCampusConnect() {
  const navigate = useNavigate();
  const [publicConfig, setPublicConfig] = useState(() => normalizePublicUiConfig(DEFAULT_PUBLIC_UI_CONFIG));

  useEffect(() => {
    let active = true;
    fetchPublicUiConfig().then((value) => { if (active) setPublicConfig(value); }).catch(() => {});
    return () => { active = false; };
  }, []);

  // Arriving from a scrolled page: start at the top.
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  const openItem = (item) => {
    const destination = item?.destination || item?.href;
    if (destination?.startsWith("/")) navigate(destination);
    else if (/^https?:\/\//i.test(destination || "")) window.open(destination, "_blank", "noopener,noreferrer");
    else if (item?.action === "home" || item?.action === "about") navigate("/");
  };

  const developers = (publicConfig.developers || [])
    .filter((developer) => developer?.enabled === true)
    .sort((a, b) => Number(a.order || 0) - Number(b.order || 0));

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=EB+Garamond:wght@400;500;600;700;800&family=DM+Sans:wght@300;400;500;600;700&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: 'DM Sans', sans-serif; background: #fff; -webkit-font-smoothing: antialiased; }
        .garamond { font-family: 'EB Garamond', Georgia, serif; }
        img { display: block; }
        .premium-section { position:relative; isolation:isolate; overflow:hidden; border-top:1px solid rgba(148,163,184,.18); border-bottom:1px solid rgba(148,163,184,.18); box-shadow:0 24px 70px rgba(15,23,42,.035); }
        .premium-section::before { content:""; position:absolute; top:0; left:50%; width:min(72%,860px); height:1px; transform:translateX(-50%); background:linear-gradient(90deg,transparent,rgba(198,40,40,.42),transparent); }
        .premium-section::after { content:""; position:absolute; inset:0; z-index:-1; pointer-events:none; opacity:.25; background-image:radial-gradient(rgba(15,23,42,.12) .55px,transparent .55px); background-size:18px 18px; mask-image:linear-gradient(to bottom,transparent,#000 18%,#000 82%,transparent); }

        .leadership-grid { grid-template-columns: 1fr 1fr; min-height: 500px; }
        .leadership-photo { height: 100%; min-height: 480px; display: block; }
        .leadership-content { padding: 60px 48px; }

        .bcc-hero { padding: 72px 24px 64px; }
        .bcc-dev-section { padding: 96px 24px; }
        .bcc-dev-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(min(300px, 100%), 1fr)); gap: 24px; }
        .bcc-dev-cell { display: flex; }
        .bcc-dev-cell > div { flex: 1; min-width: 0; }

        @media (max-width: 1023px) {
          .leadership-content { padding: 40px 32px; }
        }
        @media (min-width: 768px) and (max-width: 1023px) {
          .leadership-grid { grid-template-columns: .8fr 1fr; min-height: 420px; }
        }
        @media (max-width: 767px) {
          .leadership-grid { grid-template-columns: 1fr; min-height: auto; }
          .leadership-photo { height: auto; min-height: 0; aspect-ratio: 4 / 3; object-position: center 20%; }
          .leadership-content { padding: 32px 20px; }
          .bcc-hero { padding: 48px 20px 40px; }
          .bcc-dev-section { padding: 56px 20px; }
        }
      `}</style>

      <PublicHeader config={publicConfig} onOpen={openItem} applications={publicConfig.selector?.cards || []} />

      <main>
        {/* ══════════ INTRODUCTION ══════════ */}
        <section className="premium-section bcc-hero" style={{ background: "linear-gradient(180deg,#fff 0%,#f8fafc 100%)" }}>
          <div style={{ maxWidth: 1200, margin: "0 auto" }}>
            <FadeUp>
              <p style={{ fontSize: 12, fontWeight: 700, color: "#c62828", letterSpacing: ".16em", textTransform: "uppercase", marginBottom: 10 }}>CAMPUS CONNECT</p>
              <h1 className="garamond" style={{ fontSize: "clamp(2.2rem,5vw,3.6rem)", fontWeight: 700, color: "#111", lineHeight: 1.1, marginBottom: 14 }}>
                Behind Campus Connect
              </h1>
              <p style={{ maxWidth: 620, color: "#667085", fontSize: 15, lineHeight: 1.75 }}>
                The vision, guidance and people behind Campus Connect.
              </p>
            </FadeUp>
          </div>
        </section>

        {/* ══════════ LEADERSHIP — Dr. Meenakshi Rana ══════════ */}
        <section id="section-leadership" className="premium-section" style={{ background: "radial-gradient(circle at 85% 35%,rgba(198,40,40,.06),transparent 30%),#fff", padding: "0" }}>
          <div style={{ maxWidth: 1200, margin: "0 auto" }}>
            <FadeUp>
              <h2 className="garamond" style={{ fontSize: "clamp(1.6rem,3vw,2.4rem)", fontWeight: 700, color: "#111", textAlign: "center", padding: "60px 24px 0", letterSpacing: ".04em", textTransform: "uppercase" }}>
                Inspired by Leadership
              </h2>
            </FadeUp>

            <div className="leadership-grid" style={{ display: "grid" }}>
              {/* Left: photo — large, flush */}
              <motion.div
                initial={{ opacity: 0, scale: 1.04 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                style={{ overflow: "hidden" }}
              >
                <img
                  className="leadership-photo"
                  src="https://ik.imagekit.io/7khjnlfow/email-assets/Dr.MR.JPG"
                  alt="Dr. Meenakshi Rana"
                  style={{ width: "100%", objectFit: "cover", objectPosition: "center top" }}
                />
              </motion.div>

              {/* Right: content */}
              <FadeUp delay={0.2}>
                <div className="leadership-content" style={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
                  <p style={{ fontSize: 12, fontWeight: 600, color: "#c62828", letterSpacing: ".14em", textTransform: "uppercase", marginBottom: 16 }}>
                    Inspired by the Vision
                  </p>
                  <h2 className="garamond" style={{ fontSize: "clamp(1.8rem,3.5vw,2.8rem)", fontWeight: 700, color: "#111", marginBottom: 8, lineHeight: 1.2 }}>
                    Dr. Meenakshi Rana
                  </h2>
                  <p style={{ fontSize: 15, color: "#6b7280", marginBottom: 32, fontWeight: 500 }}>
                    Dean of Student Affairs<br />
                    Thapar Institute of Engineering &amp; Technology
                  </p>

                  {/* Quote block */}
                  <div style={{ borderLeft: "3px solid #c62828", paddingLeft: 24, marginBottom: 28 }}>
                    <p className="garamond" style={{ fontSize: 18, color: "#111", lineHeight: 1.75, fontStyle: "italic" }}>
                      "Thapar Campus Connect was envisioned to simplify student access to campus services and create a seamless digital ecosystem for the entire Thapar community."
                    </p>
                  </div>

                  <p style={{ fontSize: 14.5, color: "#4b5563", lineHeight: 1.8 }}>
                    Under her guidance, this initiative was transformed into a scalable platform connecting students, administration, and campus services — eliminating paperwork and bringing transparency to every student interaction.
                  </p>
                </div>
              </FadeUp>
            </div>
          </div>
        </section>

        {/* ══════════ DEVELOPER CARDS ══════════ */}
        <section id="section-team" className="premium-section bcc-dev-section" style={{ background: "radial-gradient(circle at 50% 0%,rgba(198,40,40,.07),transparent 30%),#fff" }}>
          <div style={{ maxWidth: 1200, margin: "0 auto" }}>
            <FadeUp>
              <h2 className="garamond" style={{ fontSize: "clamp(2rem,4vw,3rem)", fontWeight: 700, color: "#111", marginBottom: 12 }}>
                Built With Dedication By
              </h2>
              <p style={{ fontSize: "clamp(12px,1.25vw,14.5px)", color: "#6b7280", marginBottom: 48, lineHeight: 1.7 }}>
                A passionate team of developers and strategists working under the Office of Student Affairs, TIET.
              </p>
            </FadeUp>

            <div className="bcc-dev-grid">
              {developers.map((dev, i) => (
                <FadeUp key={dev.id || dev.name} delay={i * 0.08} className="bcc-dev-cell">
                  <div
                    style={{ display: "flex", flexDirection: "column", background: "#fff", borderRadius: 18, border: "1px solid #e5e7eb", overflow: "hidden", transition: "box-shadow .25s, transform .25s" }}
                    onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 12px 40px rgba(0,0,0,.1)"; e.currentTarget.style.transform = "translateY(-5px)"; }}
                    onMouseLeave={e => { e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.transform = "none"; }}
                  >
                    {/* photo strip */}
                    <div style={{ height: 280, flexShrink: 0, overflow: "hidden", position: "relative", background: "#f3f4f6" }}>
                      <img
                        src={dev.photo}
                        alt={dev.name}
                        style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center 15%", filter: "grayscale(20%)", transition: "filter .3s, transform .3s" }}
                        onMouseEnter={e => { e.currentTarget.style.filter = "grayscale(0%)"; e.currentTarget.style.transform = "scale(1.04)"; }}
                        onMouseLeave={e => { e.currentTarget.style.filter = "grayscale(20%)"; e.currentTarget.style.transform = "scale(1)"; }}
                      />
                      <span style={{ position: "absolute", top: 12, right: 12, fontSize: 10, fontWeight: 700, padding: "4px 10px", borderRadius: 20, background: "#fce8e8", color: "#c62828", border: "1px solid #c6282830" }}>
                        {dev.role}
                      </span>
                    </div>

                    {/* content */}
                    <div style={{ padding: "22px 22px 20px", display: "flex", flexDirection: "column", flex: 1 }}>
                      <h3 style={{ fontSize: 17, fontWeight: 700, color: "#111", marginBottom: 4 }}>{dev.name}</h3>
                      <p style={{ fontSize: 13, fontWeight: 600, color: "#c62828", marginBottom: 2 }}>{dev.role}</p>
                      {dev.description && <p style={{ fontSize: 12, color: "#9ca3af", marginBottom: 16 }}>{dev.description}</p>}
                      {(dev.tags || []).length > 0 && (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 18 }}>
                          {dev.tags.map((tag) => <span key={tag} style={{ fontSize: 11, padding: "3px 10px", borderRadius: 20, background: "#f3f4f6", color: "#374151", fontWeight: 500 }}>{tag}</span>)}
                        </div>
                      )}

                      {/* work */}
                      <div style={{ flex: 1 }}>
                        {dev.contribution && (
                          <div style={{ padding: "12px 14px", borderRadius: 8, background: "#fafafa", borderLeft: "3px solid #c62828", marginBottom: 16 }}>
                            <p style={{ fontSize: 12, color: "#4b5563", lineHeight: 1.5 }}>{dev.contribution}</p>
                          </div>
                        )}
                      </div>

                      {/* links */}
                      <div style={{ display: "flex", gap: 8 }}>
                        {dev.linkedin && <a href={dev.linkedin} target="_blank" rel="noopener noreferrer"
                          style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 8, background: "#0077b5", color: "#fff", fontSize: 12, fontWeight: 600, textDecoration: "none", flex: 1, justifyContent: "center" }}>
                          <Linkedin size={13} /> LinkedIn
                        </a>}
                        {dev.github && <a href={dev.github} target="_blank" rel="noopener noreferrer" style={{ padding: "8px 12px", borderRadius: 8, background: "#111", color: "#fff", fontSize: 12, textDecoration: "none" }}>GitHub</a>}
                        {dev.portfolio && <a href={dev.portfolio} target="_blank" rel="noopener noreferrer" style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #d1d5db", color: "#374151", fontSize: 12, textDecoration: "none" }}>Portfolio</a>}
                        {dev.email && <a href={`mailto:${dev.email}`} style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #d1d5db", color: "#374151", fontSize: 12, textDecoration: "none" }}>Email</a>}
                      </div>
                    </div>
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>
        </section>
      </main>

      <PublicQuickLinks config={publicConfig} onOpen={openItem} />
      <PublicPageWidgets hideFooter />
    </>
  );
}
